import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getSpeechRecognitionConstructor,
    isSilentSpeechRecognitionError,
    SPEECH_RECOGNITION_LANG
} from '../../lib/shared/speech-recognition';

const SPEECH_RECOGNITION_ERROR_TIMEOUT = 5000;

function useSpeechRecognition({ onResult }) {
    const recognitionRef = useRef(null);
    const onResultRef = useRef(onResult);
    const isListeningRef = useRef(false);
    const interimTranscriptRef = useRef('');
    const [isListening, setIsListening] = useState(false);
    const [interimTranscript, setInterimTranscript] = useState('');
    const [errorType, setErrorType] = useState(null);

    useEffect(() => {
        onResultRef.current = onResult;
    });

    useEffect(() => {
        if (!errorType) {
            return undefined;
        }

        const timeout = setTimeout(() => setErrorType(null), SPEECH_RECOGNITION_ERROR_TIMEOUT);

        return () => clearTimeout(timeout);
    }, [errorType]);

    const getRecognition = useCallback(() => {
        if (recognitionRef.current) {
            return recognitionRef.current;
        }

        const SpeechRecognitionConstructor = getSpeechRecognitionConstructor();

        if (!SpeechRecognitionConstructor) {
            return null;
        }

        const recognition = new SpeechRecognitionConstructor();

        recognition.lang = SPEECH_RECOGNITION_LANG;
        recognition.continuous = true;
        recognition.interimResults = true;

        recognition.onresult = (event) => {
            if (!isListeningRef.current) {
                return;
            }

            let nextInterimTranscript = '';

            for (let index = event.resultIndex; index < event.results.length; index++) {
                const result = event.results[index];

                if (result.isFinal) {
                    onResultRef.current(result[0].transcript);
                } else {
                    nextInterimTranscript += result[0].transcript;
                }
            }

            interimTranscriptRef.current = nextInterimTranscript;
            setInterimTranscript(nextInterimTranscript);
        };

        recognition.onerror = (event) => {
            if (isSilentSpeechRecognitionError(event.error)) {
                return;
            }

            isListeningRef.current = false;
            setIsListening(false);
            setErrorType(event.error);
        };

        recognition.onend = () => {
            interimTranscriptRef.current = '';
            setInterimTranscript('');

            if (!isListeningRef.current) {
                setIsListening(false);

                return;
            }

            try {
                recognition.start();
            } catch {
                isListeningRef.current = false;
                setIsListening(false);
            }
        };

        recognitionRef.current = recognition;

        return recognition;
    }, []);

    const startListening = useCallback(() => {
        if (isListeningRef.current) {
            return;
        }

        const recognition = getRecognition();

        if (!recognition) {
            return;
        }

        try {
            setErrorType(null);
            isListeningRef.current = true;
            recognition.start();
            setIsListening(true);
        } catch {
            isListeningRef.current = false;
            setIsListening(false);
        }
    }, [getRecognition]);

    const stopListening = useCallback(() => {
        const pendingTranscript = interimTranscriptRef.current;

        isListeningRef.current = false;
        interimTranscriptRef.current = '';
        setIsListening(false);
        setInterimTranscript('');

        if (pendingTranscript) {
            onResultRef.current(pendingTranscript);
        }

        recognitionRef.current?.stop();
    }, []);

    useEffect(() => () => {
        const recognition = recognitionRef.current;

        if (!recognition) {
            return;
        }

        isListeningRef.current = false;
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.abort();
    }, []);

    return {
        isListening,
        interimTranscript,
        errorType,
        startListening,
        stopListening
    };
}

export {
    useSpeechRecognition
};
